import {useEffect, useRef, useState} from "react";

import {HeaderInfo} from "../additional-data/ui/HeaderInfo/HeaderInfo.jsx";
import {MaterialInfoTabs} from "../additional-data/ui/MaterialInfoTabs.jsx";
import api, {MaterialsApi, TransactionsApi, WorkbenchApi} from "../../api.js";
import {operatorToProject, properties} from "@/properties.js";
import {
	Header,
	PM02Projection,
	Reservations,
	ReturnsRatio,
	Sourcing,
	StockOnHand,
	Transactions,
} from "@/variants/index.js";
import {PageTitle} from "@/components/custom/PageTitle.jsx";
import {Maintenance} from "@/components/custom/MaintenanceSwitcher/MaintenanceSwitcher.jsx";
import {useNavigate, useSearchParams} from "react-router-dom";
import {formatUsageStats, getSummaryValue, resolveUsageStartDate} from "@/helpers.js";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const indices = Object.fromEntries(months.map((month, index) => [month, (index + 1).toString().padStart(2, "0")]));

const dashboardSummaryKeys = ["Duplicate", "Obsolete", "Insurance Spare", "Slow Moving"];
const focusSummaryKeys = ["BOM Clean-up", "MM Clean-up", "No BOMs", "MIN MAX Optimise"];

export const MaterialPage = ({documentsList, image, onItemUpdate, show, getDocumentsList, images, openImageModal}) => {
	const [data, setData] = useState({
		item: {},
		items: [],
		mainColumns: [],
		equipment: [],
		hers: [],
		dashboardSummary: [],
		focusSummary: [],
		transactions: [],
		graph: [],
		sourcing: [],
		reservations: [],
		pm02Data: [],
	});

	const [commonality, setCommonality] = useState([]);
	const [structured, setStructured] = useState([]);

	const [usage, setUsage] = useState({
		usageInfo: {months: [], stats: [], count: 0, creationDate: new Date()},
		totalUsageCount: 0,
		calcStockData: {},
	});

	const [ui, setUi] = useState({
		usageLoading: false,
		graphLoading: false,
	});

	const [graphMaintenance, setGraphMaintenance] = useState(Maintenance.PM_CM);
	const [graphDataMaintenance, setGraphDataMaintenance] = useState();
	const [graphDataMaterial, setGraphDataMaterial] = useState();
	const graphMaintenanceRef = useRef(Maintenance.PM_CM);
	const graphRequestIdRef = useRef(0);
	const reviewRequestIdRef = useRef(0);

	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const operatorFromUrl = searchParams.get("operator") || "";
	const materialFromUrl = searchParams.get("material") || "";

	const headerKeys = Header.getMaterialHeaderKeys();

	function handleUsageModeChange(maintenance) {
		getUsageStat(operatorFromUrl, materialFromUrl, maintenance, data.transactions);
	}

	function handleTransactionsModeChange(maintenance) {
		const {calcStockData} = usage;

		graphMaintenanceRef.current = maintenance;
		setGraphMaintenance(maintenance);

		getSGraphData(
			operatorFromUrl,
			materialFromUrl,
			calcStockData.max || "",
			calcStockData.min || "",
			calcStockData.stock_level || 0,
			maintenance,
		);
	}

	function getEquipmentTable(operator, material) {
		api.get(`/get/equipment?operator=${encodeURIComponent(operator)}&material=${material}`)
			.then(json => {
				const equipmentData = json.data["equipment"] || [];

				setData(prev => ({
					...prev,
					equipment: equipmentData,
				}));
			})
			.catch(error => {
				console.error("Failed to fetch equipment table:", error);
				setData(prev => ({...prev, equipment: []}));
			});
	}

	function getStructured(url, operator, material) {
		api.post(`/structured/full-data?operator=${encodeURIComponent(operator)}&material=${material}`, {url: url})
			.then(json => {
				setStructured(json.data["records"]);
			})
			.catch(error => console.log(error));
	}

	function getTransactions(url, operator, material) {
		return TransactionsApi.get(Transactions.getMaterialTransactionsUrl(operator, material))
			.then(response => {
				const transactionsData = Transactions.sortTransactions(response.data["data"] || []);

				setData(prev => ({
					...prev,
					transactions: transactionsData,
				}));

				return transactionsData;
			})
			.catch(error => {
				console.log(error);
				return [];
			});
	}

	function getSGraphData(operator, material, max_, rop_, stock_level, maintenance) {
		const requestId = ++graphRequestIdRef.current;
		const requestedMaintenance = maintenance ?? null;

		setGraphDataMaintenance(undefined);
		setGraphDataMaterial(undefined);
		setUi(prev => ({...prev, graphLoading: true}));

		let url = `/transaction/graph/${material}?project_id=${operatorToProject(
			operator,
		)}&stock_level=${stock_level}&get_gr=True`;

		if (maintenance) {
			url += `&maintenance=${maintenance}`;
		}

		TransactionsApi.get(url)
			.then(json => {
				if (requestId !== graphRequestIdRef.current) {
					return;
				}

				const graph_data = (json.data.graph || []).map(item => ({
					...item,
					max: max_,
					rop: rop_,
				}));

				setData(prev => ({...prev, graph: graph_data}));
				setGraphDataMaintenance(requestedMaintenance);
				setGraphDataMaterial(material);
			})
			.catch(error => {
				console.log(error);
			})
			.finally(() => {
				if (requestId === graphRequestIdRef.current) {
					setUi(prev => ({...prev, graphLoading: false}));
				}
			});
	}

	function getUsageStat(operator, material, maintenance, transactions = []) {
		setUi(prev => ({...prev, usageLoading: true}));

		const startDate = resolveUsageStartDate(data.item?.created_date, transactions);

		TransactionsApi.get(Transactions.getMaterialUsageUrl(operator, material, maintenance))
			.then(response => {
				const {stats, totalCount, creationDate} = formatUsageStats(
					response.data["data"],
					months,
					indices,
					startDate,
				);

				setUsage(prev => ({
					...prev,
					usageInfo: {
						months,
						stats,
						count: totalCount,
						creationDate,
					},
					totalUsageCount: maintenance == null ? totalCount : prev.totalUsageCount,
				}));
			})
			.catch(error => {
				console.log(error);
			})
			.finally(() => {
				setUi(prev => ({...prev, usageLoading: false}));
			});
	}

	function getHersRecords(url, operator, material) {
		api.post(`/hers/full-data?operator=${encodeURIComponent(operator)}&material=${material}`, {url: url})
			.then(json => {
				const recordsData = json.data["records"] || [];

				setData(prev => ({
					...prev,
					hers: recordsData,
				}));
			})
			.catch(error => console.log(error));
	}

	function getSourcing(operator, material) {
		MaterialsApi.get(Sourcing.getPurchaseOrdersUrl(operator, material), {omitGlobalError: true})
			.then(response => {
				const sourcingData = response["data"] || [];

				setData(prev => ({
					...prev,
					sourcing: sourcingData,
				}));
			})
			.catch(error => console.log(error));
	}

	function getReservations(operator, material) {
		const projectId = operatorToProject(operator);

		MaterialsApi.get(`/projects/${projectId}/materials/${encodeURIComponent(material)}/reservations`)
			.then(response => {
				const reservationsData = response.data || [];

				setData(prev => ({
					...prev,
					reservations: reservationsData,
				}));
			})
			.catch(error => console.log(error));
	}

	function getLastMovementDate(transactions) {
		if (!transactions?.length) {
			return "NA";
		}
		const last_movement = transactions[0];
		return last_movement?.date ?? "NA";
	}

	function getReviewInfo(operator, material) {
		if (!process.env.REACT_APP_WORKBENCH_API_URL) {
			return;
		}

		const requestId = ++reviewRequestIdRef.current;
		const projectId = operatorToProject(operator);

		WorkbenchApi.get(`/projects/${projectId}/material-analysis/${encodeURIComponent(material)}/review`, {
			omitGlobalError: true,
		})
			.then(response => {
				if (requestId !== reviewRequestIdRef.current) {
					return;
				}

				const review = response.data || {};

				setData(prev => {
					if (prev.item?.material !== material || prev.item?.operator !== operator) {
						return prev;
					}

					return {
						...prev,
						item: {
							...prev.item,
							is_reviewed: review.is_reviewed,
							review_date: review.review_date,
							review_expiry_date: review.review_expiry_date,
							review_notes: review.review_notes,
							reviewed_by: review.reviewed_by,
						},
					};
				});
			})
			.catch(() => {});
	}

	function getReturnsRatio(operator, material) {
		TransactionsApi.get(`/inventory/returns_ratio?materials=${material}&operator=${operator}`)
			.then(response => {
				const responseData = response.data;
				const newReturnRatio =
					Array.isArray(responseData) && responseData.length > 0 ? responseData[0].return_ratio : null;

				setData(prev => ({
					...prev,
					item: {
						...prev.item,
						return_ratio: newReturnRatio,
					},
				}));
			})
			.catch(error => console.log(error));
	}

	function getPM02Projection(operator, material) {
		const projectId = operatorToProject(operator);

		MaterialsApi.get(`/projects/${projectId}/materials/${encodeURIComponent(material)}/task-lists`)
			.then(response => {
				const responsePm02Data = response.data || [];

				setData(prev => ({
					...prev,
					pm02Data: responsePm02Data,
				}));
			})
			.catch(error => console.log(error));
	}

	function sendRequest(url, operator, material) {
		api.post(`/search/report?operator=${encodeURIComponent(operator)}&material=${material}&showAll=${true}`, {url})
			.then(json => {
				const filteredData = json.data.filter(v => !(v.operator === operator && v.material === material));
				setCommonality(filteredData);
			})
			.catch(() => {
				console.error("Commonality fetch failed:", error);
				setCommonality([]);
			});
	}

	useEffect(() => {
		let url = properties.apiUrl;
		if (sessionStorage.getItem("item_url") && sessionStorage.getItem("item_url") !== null) {
			url = sessionStorage.getItem("item_url");
		} else if (localStorage.getItem("item_url") && localStorage.getItem("item_url") !== null) {
			url = localStorage.getItem("item_url");
			sessionStorage.setItem("item_url", url);
		}

		api.post(`/item/full?material=${materialFromUrl}&operator=${encodeURIComponent(operatorFromUrl)}`, {url}, { omitGlobalError: true })
			.then(json => {
				const rawItems = json.data.item;
				const firstItem = rawItems[0] || {type: "material"};

				if (firstItem && !firstItem.type) {
					firstItem.type = "material";
				}

				const StockOnHandCalcData = StockOnHand.calculateAllRows(rawItems, firstItem);

				setData(prev => ({
					...prev,
					item: firstItem,
					items: rawItems,
					mainColumns: json.data.mainColumns,
					dashboardSummary: getSummaryValue(dashboardSummaryKeys, {}),
					focusSummary: getSummaryValue(focusSummaryKeys, {}),
				}));

				setUsage(prev => ({
					...prev,
					calcStockData: StockOnHandCalcData,
				}));

				onItemUpdate(firstItem.part_number);
			})
			.catch(() => {
				if (!properties.isDebug) {
					navigate(`/search?search=${materialFromUrl}`);
				}
			});
	}, [operatorFromUrl, materialFromUrl]);

	useEffect(() => {
		if (!data.item?.material) return;

		const url = sessionStorage.getItem("item_url");

		sendRequest(url, operatorFromUrl, materialFromUrl);
		getEquipmentTable(operatorFromUrl, materialFromUrl);
		getStructured(url, operatorFromUrl, materialFromUrl);
		getTransactions(url, operatorFromUrl, materialFromUrl).then(transactions =>
			getUsageStat(operatorFromUrl, materialFromUrl, null, transactions),
		);

		if (Reservations?.enabled) {
			getReservations(operatorFromUrl, materialFromUrl);
		}

		getHersRecords(url, operatorFromUrl, materialFromUrl);

		if (Sourcing?.enabled) {
			getSourcing(operatorFromUrl, materialFromUrl);
		}

		if (PM02Projection?.enabled) {
			getPM02Projection(operatorFromUrl, materialFromUrl);
		}

		if (ReturnsRatio?.enabled) {
			getReturnsRatio(operatorFromUrl, materialFromUrl);
		}

		getReviewInfo(operatorFromUrl, materialFromUrl);
	}, [data.item.material]);

	useEffect(() => {
		const {calcStockData} = usage;

		if (!data.item?.material || (!calcStockData?.stock_level && calcStockData?.stock_level !== 0)) return;

		getSGraphData(
			operatorFromUrl,
			materialFromUrl,
			calcStockData.max || "",
			calcStockData.min || "",
			calcStockData.stock_level || 0,
			graphMaintenanceRef.current,
		);
	}, [usage.calcStockData, data.item?.material]);

	useEffect(() => {
		reviewRequestIdRef.current += 1;
		setGraphDataMaintenance(undefined);
		setGraphDataMaterial(undefined);
	}, [materialFromUrl]);

	const item = {...data.item};
	if (item && !item.hasOwnProperty("raw")) item.raw = item.description;

	const pageTitleText = item.material || materialFromUrl;

	return (
		<div data-testid="material-page-container">
			<PageTitle title={pageTitleText} />
			<HeaderInfo
				image={image}
				item={item}
				headerColumns={headerKeys}
				images={images}
				usage={usage.usageInfo.count || 0}
				handleModalOpen={() => openImageModal()}
				showEdit={show}
			/>
			<MaterialInfoTabs
				item={{
					...item,
					last_movement: getLastMovementDate(data.transactions),
				}}
				structured={structured}
				equipment={data.equipment}
				dashboardSummary={data.dashboardSummary}
				transactionsInfo={{
					tableData: data.transactions,
					graphData: data.graph,
					graphMaintenance,
					graphDataMaintenance,
					graphDataMaterial,
					graphLoading: ui.graphLoading,
					material: materialFromUrl,
				}}
				hers={data.hers}
				updateDocsTable={getDocumentsList}
				documentsList={documentsList}
				focusSummary={data.focusSummary}
				usageInfo={usage.usageInfo}
				loading={ui.usageLoading}
				totalUsageCount={usage.totalUsageCount}
				calcStockData={usage.calcStockData}
				onUsageChange={handleUsageModeChange}
				onTransactionsChange={handleTransactionsModeChange}
				stockData={data.items}
				commonality={commonality}
				sourcingInfo={data.sourcing}
				reservations={data.reservations}
				pm02Data={data.pm02Data}
			/>
		</div>
	);
};
